<app-dialog [closeValue]="closeResult()" [interceptClose]="true" (closeRequested)="requestClose()">
  <app-dialog-title>{{
    (isEdit ? 'SHARED.QUESTIONS.EDIT_QUESTION' : 'SHARED.QUESTIONS.ADD_QUESTION') | translate
  }}</app-dialog-title>

  <app-dialog-content>
    <div class="add-question">
      <div class="add-question__form">
        @if (lockedInfoMessage) {
          <div class="add-question__notice">
            <app-info-form-box>{{ lockedInfoMessage }}</app-info-form-box>
          </div>
        }

        <!-- Step 1 — Category -->
        <section class="add-question__step">
          <div class="add-question__step-head">
            <span class="add-question__step-index">1</span>
            <span class="add-question__step-title">{{ I18N + '.STEP_CATEGORY' | translate }}</span>
            <span class="add-question__step-hint">{{ I18N + '.CATEGORY_HINT' | translate }}</span>
          </div>
          <div
            class="add-question__chips"
            role="group"
            [attr.aria-label]="I18N + '.STEP_CATEGORY' | translate">
            @for (chip of categoryChips(); track chip.uuid) {
              <button
                type="button"
                class="add-question__chip"
                [class.add-question__chip--selected]="chip.uuid === selectedCategoryUuid()"
                [disabled]="isEdit"
                [attr.aria-pressed]="chip.uuid === selectedCategoryUuid()"
                (click)="selectCategory(chip.uuid)"
                [attr.data-testid]="'category-chip-' + chip.uuid">
                <span>{{ chip.name }}</span>
                <span class="add-question__chip-count">{{ chip.count }}</span>
              </button>
            }
          </div>
          @if (!isEdit && selectedCategory()) {
            <p class="add-question__muted">{{ shareText() }}</p>
          }
        </section>

        <mat-divider />

        <!-- Step 2 — Default language -->
        <section class="add-question__step">
          <div class="add-question__step-head">
            <span class="add-question__step-index">2</span>
            <span class="add-question__step-title">{{
              I18N + '.STEP_WRITE' | translate: { language: defaultLanguage().name }
            }}</span>
            <span class="add-question__default-badge">{{
              I18N + '.DEFAULT_LANGUAGE' | translate
            }}</span>
          </div>

          <mat-form-field
            appearance="outline"
            class="add-question__default-field"
            subscriptSizing="dynamic">
            <mat-label>{{ defaultStemLabel() }}</mat-label>
            <input
              #defaultInput
              matInput
              type="text"
              [maxlength]="maxLength"
              [ngModel]="defaultValue()"
              (ngModelChange)="onDefaultInput($event)"
              [placeholder]="defaultPlaceholder"
              [disabled]="readonlyLangs.includes(defaultLanguage().code)"
              [attr.aria-label]="
                I18N + '.STEP_WRITE' | translate: { language: defaultLanguage().name }
              "
              data-testid="input-question-default" />
            <mat-hint align="end">{{ defaultValue().length }}/{{ maxLength }}</mat-hint>
          </mat-form-field>

          @if (hasQuestionMark()) {
            <div
              class="add-question__warning add-question__warning--info"
              role="status"
              data-testid="qmark-warning">
              <mat-icon aria-hidden="true">lightbulb_outline</mat-icon>
              <span>{{ I18N + '.QMARK_WARNING' | translate }}</span>
            </div>
          }

          @if (duplicateQuestion(); as duplicate) {
            <div
              class="add-question__warning add-question__warning--info"
              role="status"
              data-testid="duplicate-warning">
              <mat-icon aria-hidden="true">warning_amber</mat-icon>
              <span>{{ I18N + '.DUPLICATE_WARNING' | translate: { question: duplicate } }}</span>
            </div>
          }

          @if (existingQuestionTexts().length > 0) {
            <button
              type="button"
              class="add-question__link"
              (click)="toggleShowExisting()"
              [attr.aria-expanded]="showExisting()"
              data-testid="toggle-existing-questions">
              <span>{{
                (showExisting() ? I18N + '.HIDE_EXISTING' : I18N + '.SHOW_EXISTING')
                  | translate
                    : { count: existingQuestionTexts().length, category: selectedCategory()?.name }
              }}</span>
              <mat-icon aria-hidden="true">{{
                showExisting() ? 'expand_less' : 'expand_more'
              }}</mat-icon>
            </button>
            @if (showExisting()) {
              <div
                class="add-question__collapse"
                animate.enter="add-question__collapse--enter"
                animate.leave="add-question__collapse--leave">
                <ul class="add-question__existing">
                  @for (question of existingQuestionTexts(); track $index) {
                    <li>{{ question }}</li>
                  }
                </ul>
              </div>
            }
          }
        </section>

        <mat-divider />

        <!-- Step 3 — Translations -->
        <section class="add-question__step">
          <div class="add-question__step-head">
            <span class="add-question__step-index">3</span>
            <span class="add-question__step-title">{{
              I18N + '.STEP_TRANSLATIONS' | translate
            }}</span>
          </div>
          <p class="add-question__muted">{{ translationsStatus() }}</p>

          <app-translation-language-list
            [defaultLanguage]="null"
            [defaultValue]="defaultValue()"
            [languages]="dialogLanguages()"
            [inactiveLanguages]="inactiveLanguages()"
            [values]="translationValues()"
            (valuesChange)="onTranslationsChange($event)"
            [readonlyLangs]="readonlyLangs"
            [stemByCode]="stemByCode()"
            [aiTranslatedCodes]="aiTranslatedCodes()"
            [failedTranslations]="failedTranslations()"
            [fieldLabel]="'SHARED.SYSTEM.QUESTION' | translate"
            [maxLength]="maxLength"
            testIdPrefix="input-question-language"
            [isSuggesting]="isSuggesting()"
            [aiAvailable]="isAiEnabled()"
            (suggestAll)="suggestAll()" />
        </section>

        @if (!isEdit) {
          <mat-divider />

          <!-- Step 4 — Activation -->
          <section class="add-question__step">
            <div class="add-question__step-head">
              <span class="add-question__step-index">4</span>
              <span class="add-question__step-title">{{
                I18N + '.STEP_ACTIVATION' | translate
              }}</span>
              <span class="add-question__step-hint">{{ activationHint() }}</span>
            </div>

            <div class="add-question__toggle-row">
              <div class="add-question__toggle-text">
                <div class="add-question__toggle-title">
                  {{ I18N + '.ACTIVE_TITLE' | translate }}
                </div>
                <div class="add-question__muted">
                  {{ I18N + '.ACTIVE_DESC' | translate: { category: selectedCategory()?.name } }}
                </div>
              </div>
              <mat-slide-toggle
                [checked]="effectiveActive()"
                [disabled]="!canActivate()"
                (change)="onActiveChange($event.checked)"
                data-testid="toggle-active"
                [aria-label]="I18N + '.ACTIVE_TITLE' | translate" />
            </div>

            <div class="add-question__toggle-row">
              <div class="add-question__toggle-text">
                <div
                  class="add-question__toggle-title"
                  [class.add-question__toggle-title--muted]="!effectiveActive()">
                  {{ I18N + '.ENFORCE_TITLE' | translate }}
                </div>
                <div class="add-question__muted">{{ I18N + '.ENFORCE_DESC' | translate }}</div>
              </div>
              <mat-slide-toggle
                [checked]="effectiveEnforced()"
                [disabled]="!effectiveActive()"
                (change)="onEnforceChange($event.checked)"
                data-testid="toggle-enforce"
                [aria-label]="I18N + '.ENFORCE_TITLE' | translate" />
            </div>
          </section>
        }
      </div>

      <aside class="add-question__preview">
        <app-question-guest-preview
          [groups]="questionCategories()"
          [selectedCategoryUuid]="selectedCategoryUuid()"
          [newQuestionByLang]="newQuestionByLang()"
          [defaultLanguageCode]="defaultLanguage().code"
          [activeLanguages]="previewLanguages()"
          [stemByCode]="stemByCode()" />
      </aside>
    </div>
  </app-dialog-content>

  <app-dialog-actions>
    <span class="add-question__ready">{{ readyText() }}</span>

    <button
      matButton="outlined"
      type="button"
      data-testid="btn-cancel-question-dialog"
      (click)="cancel()">
      {{ 'SHARED.SYSTEM.CANCEL' | translate }}
    </button>

    @if (isEdit) {
      <button
        matButton="filled"
        type="button"
        data-testid="btn-submit-question"
        [disabled]="!canSave()"
        (click)="save(false)">
        {{ 'SHARED.SYSTEM.SAVE_CHANGES' | translate }}
      </button>
    } @else {
      <button
        matButton="outlined"
        type="button"
        data-testid="btn-save-add-another"
        [disabled]="!canSave()"
        (click)="save(true)">
        {{ 'SHARED.QUESTIONS.SAVE_AND_ADD_ANOTHER' | translate }}
      </button>
      <button
        matButton="filled"
        type="button"
        data-testid="btn-submit-question"
        [disabled]="!canSave()"
        (click)="save(false)">
        {{ 'SHARED.QUESTIONS.ADD_QUESTION' | translate }}
      </button>
    }
  </app-dialog-actions>
</app-dialog>
